/**
 * useHarperNarration — Pre-recorded Harper Kael voice narration for the landing page.
 *
 * Plays cached MP3 files generated by ElevenLabs cloned voice.
 * Each section plays once per session. Sections queue in order.
 * Falls back gracefully if audio files fail to load.
 */

import { useState, useCallback, useRef, useEffect } from 'react';

const STORAGE_KEY = 'rigel-harper-narration';

// Pre-recorded narration files (Harper Kael voice via ElevenLabs)
const NARRATION_FILES = {
  hero: '/assets/audio/narration/landing_hero.mp3',
  world: '/assets/audio/narration/landing_world.mp3',
  gameplay: '/assets/audio/narration/landing_gameplay.mp3',
  zones: '/assets/audio/narration/landing_zones.mp3',
  pricing: '/assets/audio/narration/landing_pricing.mp3',
  cta: '/assets/audio/narration/landing_cta.mp3',
};

export default function useHarperNarration() {
  const [narrationEnabled, setNarrationEnabled] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(STORAGE_KEY) === 'true';
  });
  const [playing, setPlaying] = useState(false);
  const [currentSection, setCurrentSection] = useState(null);

  const audioRef = useRef(null);
  const playedSections = useRef(new Set());
  const queueRef = useRef([]);
  const playingRef = useRef(false);

  // Persist preference
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, narrationEnabled ? 'true' : 'false');
    }
  }, [narrationEnabled]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const playNext = useCallback(() => {
    if (queueRef.current.length === 0) {
      playingRef.current = false;
      setPlaying(false);
      setCurrentSection(null);
      return;
    }

    const sectionId = queueRef.current.shift();
    const src = NARRATION_FILES[sectionId];
    if (!src) {
      playNext();
      return;
    }

    playingRef.current = true;
    setPlaying(true);
    setCurrentSection(sectionId);

    const audio = new Audio(src);
    audioRef.current = audio;

    audio.addEventListener('ended', () => {
      setPlaying(false);
      setCurrentSection(null);
      playingRef.current = false;
      // Small pause between sections
      setTimeout(() => playNext(), 600);
    });

    audio.addEventListener('error', () => {
      console.warn(`Harper narration failed for: ${sectionId}`);
      setPlaying(false);
      setCurrentSection(null);
      playingRef.current = false;
      playNext();
    });

    audio.play().catch(() => {
      // Autoplay blocked — user interaction needed
      console.warn('Harper narration autoplay blocked');
      setPlaying(false);
      playingRef.current = false;
    });
  }, []);

  const playSection = useCallback((sectionId) => {
    if (!narrationEnabled) return;
    if (playedSections.current.has(sectionId)) return;
    if (!NARRATION_FILES[sectionId]) return;

    playedSections.current.add(sectionId);

    // Add to queue
    if (!queueRef.current.includes(sectionId)) {
      queueRef.current.push(sectionId);
    }

    // Start playing if not already
    if (!playingRef.current) {
      playNext();
    }
  }, [narrationEnabled, playNext]);

  const stopNarration = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    queueRef.current = [];
    playingRef.current = false;
    setPlaying(false);
    setCurrentSection(null);
  }, []);

  const toggle = useCallback(() => {
    setNarrationEnabled(prev => {
      const next = !prev;
      if (!next) {
        stopNarration();
      }
      if (next) {
        // Reset played sections so narration can start fresh
        playedSections.current.clear();
      }
      return next;
    });
  }, [stopNarration]);

  return {
    narrationEnabled,
    playing,
    currentSection,
    toggle,
    playSection,
    stop: stopNarration,
  };
}
